import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Upload,
  X,
  AlertCircle,
} from "lucide-react";
import SupabaseStorageService from "../../services/supabaseStorageService";
import { supabase } from "../../lib/supabase";
// Temporarily using simplified version for debugging PDF issues
import FilePreviewModalSimple from "./FilePreviewModalSimple";
import ConfirmDialog from "./ConfirmDialog";
import "./FileUpload.css";

const FileUpload = ({
  files = [],
  onFilesChange,
  projectId,
  clientId = null, // Can be passed directly or fetched from project
  disabled = false,
  maxFiles = 10,
  type = 'project', // 'project' or 'takeoff'
  uploadDate = null, // Custom upload date (optional)
  showFileList = true, // Whether to show the uploaded files list
}) => {
  console.log("FileUpload component rendered with:", {
    projectId,
    clientId,
    filesCount: files ? files.length : 0,
    files: files,
  });
  
  // Debug: Log detailed file structure
  if (files && files.length > 0) {
    console.log("First file structure:", files[0]);
    console.log("File keys:", Object.keys(files[0]));
    files.forEach((file, index) => {
      console.log(`File ${index}:`, {
        id: file.id,
        url: file.url,
        publicUrl: file.publicUrl,
        public_url: file.public_url,
        direct_download_url: file.direct_download_url,
        signed_url: file.signed_url,
        filename: file.filename,
        original_filename: file.original_filename,
        allKeys: Object.keys(file)
      });
    });
  }
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [projectClientId, setProjectClientId] = useState(clientId);
  const [confirmRemove, setConfirmRemove] = useState({
    isOpen: false,
    fileId: null,
    fileName: "",
  });
  const [abortControllers, setAbortControllers] = useState(new Map());
  const fileInputRef = useRef(null);

  // Update projectClientId when clientId prop changes
  useEffect(() => {
    if (clientId) {
      setProjectClientId(clientId);
    }
  }, [clientId]);

  // Fetch client ID from project if not provided
  useEffect(() => {
    const fetchClientId = async () => {
      if (!clientId && projectId) {
        try {
          const { data } = await supabase
            .from("projects")
            .select("client_id")
            .eq("id", projectId)
            .single();

          if (data && data.client_id) {
            setProjectClientId(data.client_id);
          }
        } catch (err) {
          console.error("Error fetching client ID:", err);
        }
      }
    };

    fetchClientId();
  }, [projectId, clientId]);

  // Handle drag events
  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      if (!disabled) {
        setDragOver(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    // Reset input value to allow selecting same file again
    e.target.value = "";
  };

  // Process selected files - NOT using useCallback to ensure we always have current projectId
  const handleFiles = async (newFiles) => {
    console.log(
      "FileUpload handleFiles - projectId:",
      projectId,
      "clientId prop:",
      clientId,
      "projectClientId state:",
      projectClientId,
    );

    // Check if projectId exists and is valid (not null, undefined, or empty string)
    if (!projectId || projectId === "") {
      setErrors({ general: "Please select a project first" });
      return;
    }

    // Use clientId prop directly if available, otherwise use projectClientId state
    const effectiveClientId = clientId || projectClientId;
    console.log("Using effectiveClientId:", effectiveClientId);

    // Check if we have a client ID
    if (!effectiveClientId) {
      setErrors({
        general:
          "Unable to determine client for file organization. Please try again.",
      });
      return;
    }

    if (files.length + newFiles.length > maxFiles) {
      setErrors({ general: `Maximum ${maxFiles} files allowed` });
      return;
    }

    setErrors({});

    const uploadPromises = newFiles.map(async (file) => {
      try {
        // Validate file
        const validation = SupabaseStorageService.validateFile(file);
        if (!validation.isValid) {
          setErrors((prev) => ({ ...prev, [file.name]: validation.error }));
          return null;
        }

        // Create abort controller for this file
        const controller = new AbortController();
        setAbortControllers(prev => {
          const newMap = new Map(prev);
          newMap.set(file.name, controller);
          return newMap;
        });

        // Upload file with progress tracking
        const fileMetadata = await SupabaseStorageService.uploadFile(
          file,
          projectId,
          effectiveClientId,
          (progress) => {
            setUploadProgress((prev) => ({ ...prev, [file.name]: progress }));
          },
          controller,
          type,
          uploadDate
        );

        // Clear progress and abort controller after successful upload
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        
        setAbortControllers(prev => {
          const newMap = new Map(prev);
          newMap.delete(file.name);
          return newMap;
        });

        return fileMetadata;
      } catch (error) {
        console.error("Upload error:", error);
        
        // Clean up abort controller on error
        setAbortControllers(prev => {
          const newMap = new Map(prev);
          newMap.delete(file.name);
          return newMap;
        });
        
        setErrors((prev) => ({ ...prev, [file.name]: error.message }));
        return null;
      }
    });

    try {
      const uploadedFiles = await Promise.all(uploadPromises);
      const successfulUploads = uploadedFiles.filter(Boolean);

      console.log("Upload complete:", {
        totalUploaded: uploadedFiles.length,
        successful: successfulUploads.length,
        currentFiles: files.length,
        newFiles: successfulUploads,
        combinedFiles: [...files, ...successfulUploads],
      });

      if (successfulUploads.length > 0) {
        const allFiles = [...files, ...successfulUploads];
        console.log("Calling onFilesChange with:", allFiles);
        onFilesChange(allFiles);
      }
    } catch (error) {
      console.error("Upload batch error:", error);
      setErrors({ general: "Some files failed to upload" });
    }
  };

  // Show remove confirmation dialog
  // Use url or path as unique identifier since some files don't have id
  const handleRemoveFile = useCallback(
    (fileIdentifier) => {
      const fileToRemove = files.find(
        (file) => (file.id || file.url || file.path) === fileIdentifier
      );
      if (!fileToRemove) return;

      setConfirmRemove({
        isOpen: true,
        fileId: fileIdentifier,
        fileName: fileToRemove.filename || fileToRemove.original_filename,
      });
    },
    [files],
  );

  // Confirm file removal
  const confirmFileRemoval = useCallback(() => {
    if (confirmRemove.fileId) {
      const updatedFiles = files.filter(
        (file) => (file.id || file.url || file.path) !== confirmRemove.fileId,
      );
      onFilesChange(updatedFiles);
    }
    setConfirmRemove({ isOpen: false, fileId: null, fileName: "" });
  }, [confirmRemove.fileId, files, onFilesChange]);

  // Cancel file removal
  const cancelFileRemoval = useCallback(() => {
    setConfirmRemove({ isOpen: false, fileId: null, fileName: "" });
  }, []);

  // Cancel file upload
  const cancelUpload = useCallback((filename) => {
    const controller = abortControllers.get(filename);
    if (controller) {
      controller.abort();
      
      // Remove from abort controllers
      setAbortControllers(prev => {
        const newMap = new Map(prev);
        newMap.delete(filename);
        return newMap;
      });
      
      // Remove from upload progress
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[filename];
        return newProgress;
      });
      
      // Add error message
      setErrors(prev => ({ 
        ...prev, 
        [filename]: "Upload cancelled" 
      }));
    }
  }, [abortControllers]);

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
    setPreviewFile(null);
  }, []);

  // Open file browser
  const openFileBrowser = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="file-upload-container">
      {/* Upload Area */}
      <div
        className={`file-upload-area ${dragOver ? "drag-over" : ""} ${disabled ? "disabled" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileBrowser}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="*"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={disabled}
        />

        <div className="upload-content">
          <Upload className="upload-icon" size={48} />
          <div className="upload-text">
            <p className="upload-primary">Drag files here or click to browse</p>
            <p className="upload-secondary">
              All file formats accepted, up to 2GB each
            </p>
          </div>
        </div>
      </div>

      {/* Error Messages */}
      {Object.keys(errors).length > 0 && (
        <div className="upload-errors">
          {Object.entries(errors).map(([key, error]) => (
            <div key={key} className="error-message">
              <AlertCircle size={16} />
              <span>{key === "general" ? error : `${key}: ${error}`}</span>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="upload-progress-container">
          {Object.entries(uploadProgress).map(([filename, progress]) => (
            <div key={filename} className="upload-progress-item">
              <div className="progress-info">
                <span className="progress-filename">{filename}</span>
                <div className="progress-actions">
                  <span className="progress-percent">{progress}%</span>
                  <button
                    className="cancel-upload-btn"
                    title="Cancel upload"
                    onClick={() => cancelUpload(filename)}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files List */}
      {showFileList && files.length > 0 && (
        <div className="uploaded-files-container">
          <h4 className="uploaded-files-title">
            Uploaded Files ({files.length})
          </h4>
          <div className="uploaded-files-list">
            {files.map((file) => (
              <div key={file.id} className="uploaded-file-item-simple">
                <div className="file-name-simple" title={file.filename || file.original_filename}>
                  {file.filename || file.original_filename}
                </div>
                <div className="file-actions-simple">
                  {/* Download link */}
                  {(file.url || file.direct_download_url || file.public_url || file.signed_url) ? (
                    <a
                      href={
                        file.url ||
                        file.direct_download_url ||
                        file.public_url ||
                        file.signed_url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-view-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Download
                    </a>
                  ) : (
                    <span
                      className="file-view-link"
                      style={{ color: 'var(--gray-500)', cursor: 'not-allowed' }}
                      title="No download URL available"
                    >
                      No URL
                    </span>
                  )}
                  {/* Remove button */}
                  <button
                    className="file-remove-btn-simple"
                    title="Remove file"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(file.id || file.url || file.path);
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Preview Modal - Using simplified version for PDF debugging */}
      <FilePreviewModalSimple
        file={previewFile}
        isOpen={showPreview}
        onClose={handleClosePreview}
      />

      {/* Confirm Remove Dialog */}
      <ConfirmDialog
        isOpen={confirmRemove.isOpen}
        title="Remove File"
        message={`Are you sure you want to remove "${confirmRemove.fileName}"?\n\nThis will remove it from the current form but won't delete it from storage.`}
        onConfirm={confirmFileRemoval}
        onCancel={cancelFileRemoval}
        confirmText="Remove File"
        cancelText="Keep File"
      />
    </div>
  );
};

export default FileUpload;
